'use client'

import { Box, chakra } from '@chakra-ui/react'
import {
  ButtonTypes,
  MediaVideoProps,
  videoCompleteEvent,
  videoProgressEvent,
  videoStartEvent,
  VideoTrackingProps,
} from '@/baseComponents'
import { Button } from '../button'
import { FaPause, FaPlay } from 'react-icons/fa'
import { useMemo, useRef, useState } from 'react'
import { MediaTypes } from '@/types/enums'
import { MediaData } from './MediaData'

export interface CustomMediaVideoProps extends MediaVideoProps, MediaVideoData {
  hasBorderRadius?: boolean
  forceControls?: boolean
}

export interface MediaVideoData extends MediaData {
  type?: MediaTypes.Video
  videoSrc: string
  isAutoPlay?: boolean
  showControls?: boolean
  isFullWidth?: boolean
}

export default chakra(MediaVideo)
function MediaVideo({
  type = MediaTypes.Video,
  hasBorderRadius = false,
  showControls = true,
  forceControls = false,
  theme = 'brand',
  videoSrc,
  isFullWidth,
  isAutoPlay,
  ...props
}: CustomMediaVideoProps) {
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false)
  const [videoStartedOnce, SetHasVideoStartedOnce] = useState<boolean>(false)
  const [isOverlayHidden, setIsOverlayHidden] = useState<boolean>(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const defaultVideoTrackingProps: VideoTrackingProps = {
    video_title: videoSrc,
    video_url: videoSrc,
    video_duration: videoRef.current ? videoRef.current.duration : 0,
    video_provider: 'backoffice',
  }

  useMemo(() => {
    isVideoPlaying ? videoRef?.current?.play() : videoRef?.current?.pause()
  }, [isVideoPlaying])

  useMemo(() => {
    setIsOverlayHidden(isVideoPlaying || forceControls || (isAutoPlay ?? false))
  }, [isVideoPlaying, isAutoPlay, forceControls])

  function videoStartTrackingEvent() {
    if (videoStartedOnce || !videoRef.current) {
      return
    }

    SetHasVideoStartedOnce(true)
    videoStartEvent(videoRef, defaultVideoTrackingProps)
  }

  function videoEndedEvent() {
    setIsVideoPlaying(false)
    videoCompleteEvent(videoRef, defaultVideoTrackingProps)
  }

  return (
    <Box
      position='relative'
      role='group'
      _before={
        isOverlayHidden
          ? undefined
          : {
              position: 'absolute',
              content: '""',
              top: 0,
              left: 0,
              width: 'full',
              height: 'full',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              borderRadius: isFullWidth
                ? '0 0.75rem 0.75rem 0'
                : hasBorderRadius
                  ? 'xl'
                  : 'none',
            }
      }
      {...props}
    >
      <chakra.video
        ref={videoRef}
        src={videoSrc}
        autoPlay={isAutoPlay}
        loop={props.loop}
        muted={props.isMuted}
        onPlay={() => videoStartTrackingEvent()}
        controls={forceControls}
        onTimeUpdate={() =>
          videoProgressEvent(videoRef, defaultVideoTrackingProps)
        }
        width={'full'}
        height={'full'}
        onEnded={() => videoEndedEvent()}
        controlsList='nodownload'
        borderRadius={
          isFullWidth ? '0 0.75rem 0.75rem 0' : hasBorderRadius ? 'xl' : 'none'
        }
      >
        <source src={videoSrc} type='video/mp4' />
        Your browser does not support the video tag.
      </chakra.video>

      {showControls && !forceControls && (
        <Button
          buttonType={ButtonTypes.IconOnly}
          icon={isVideoPlaying ? <FaPause /> : <FaPlay />}
          ariaLabel={isVideoPlaying ? 'Pause' : 'Play'}
          iconProps={{
            'aria-label': '',
            position: 'absolute',
            top: 'calc(50% - 2rem)',
            left: 'calc(50% - 2rem)',
            width: 16,
            height: 16,
            borderRadius: 'full',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: `${theme}.white`,
            visibility: isVideoPlaying ? 'hidden' : 'visible',
            opacity: isVideoPlaying ? '0' : '1',
            transition: 'visibility 0.2s ease-in-out, opacity 0.2s ease-in-out',
            color: `${theme}.neutrals.850`,
            _groupHover: { visibility: 'visible', opacity: '1' },
            _hover: {
              backgroundColor: `${theme}.neutrals.25`,
            },
          }}
          onClick={() => setIsVideoPlaying(!isVideoPlaying)}
        />
      )}
    </Box>
  )
}
